import { Form, Table, Typography, DatePicker } from 'antd'

import { Link } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import dayjs from 'dayjs'
import { useAsync, useIsMobile } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import {
  useFetchReminders,
  useReminders,
  useRemindersPagination,
} from '../../redux/slices/reminders'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import { standardDateTime } from '../../lib/datetime'
import Filters from '../Forms/Filters'
import {
  EXPAND_COLUMN_WIDTH,
  messageTypeLabel,
  ReminderMobileCard,
  ReminderMobileDetails,
  ReminderStatusText,
} from './ReminderMobileRow'

const { Text } = Typography
const { RangePicker } = DatePicker
const { Item } = Form

const QUERY_PARAMS_SCHEMA = {
  page: Number,
  fromDate: String,
  toDate: String,
}

const useStyles = createUseStyles({
  filterItem: {
    flex: 1,
    minWidth: 150,
  },
})

// Hoisted to module scope: an arrow declared inside the component would trip
// react/no-unstable-nested-components when passed as `expandedRowRender`.
const renderMobileDetails = () => reminder =>
  <ReminderMobileDetails reminder={reminder} />

export default function Reminders() {
  const classes = useStyles()
  const isMobile = useIsMobile()

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1 },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const { pending, error, clearError } = useAsync(
    useFetchReminders(conditions, true),
    true,
  )

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const handleSubmit = useCallback(
    ({ reminderStatus, reminderType }) => {
      setConditions({
        ...conditions,
        page: 1,
        reminderStatus,
        reminderType,
      })
    },
    [setConditions, conditions],
  )

  const handleDateChange = useCallback(
    (_, rangeString) => {
      if (rangeString[0] === '' || rangeString[1] === '') return

      setConditions({
        ...conditions,
        fromDate: rangeString[0],
        toDate: rangeString[1],
        page: 1,
      })
    },
    [setConditions, conditions],
  )

  const handleReset = useCallback(() => {
    setConditions({ page: 1 })
  }, [setConditions])

  const rangeFilter = () => {
    const value =
      conditions.fromDate && conditions.toDate
        ? [dayjs(conditions.fromDate), dayjs(conditions.toDate)]
        : null

    return (
      <Item className={classes.filterItem}>
        <RangePicker
          value={value}
          ranges={{
            'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
          }}
          onChange={handleDateChange}
        />
      </Item>
    )
  }

  const columns = [
    {
      title: 'Patient',
      dataIndex: ['patient', 'id'],
      key: 'patientId',
      render(text, reminder) {
        if (!reminder.patient) return '-'
        return (
          <Link to={toPath.patients.patient({ id: reminder.patient.id })}>
            {reminder.patient.lastName}
            {', '}
            {reminder.patient.firstName}
          </Link>
        )
      },
    },
    {
      title: 'Type',
      dataIndex: ['messageType'],
      key: 'messageType',
      render(text) {
        return messageTypeLabel(text)
      },
    },
    {
      title: 'To',
      dataIndex: ['to'],
      key: 'phoneTo',
    },
    {
      title: 'Status',
      dataIndex: ['status'],
      key: 'status',
      render(text) {
        return <ReminderStatusText status={text} />
      },
    },
    {
      title: 'Sent on',
      dataIndex: ['createdAt'],
      key: 'createdAt',
      render(text, reminder) {
        if (!reminder.createdAt) return ''
        return standardDateTime(reminder.createdAt)
      },
    },
    {
      title: 'Updated on',
      dataIndex: ['updatedAt'],
      key: 'updatedAt',
      render(text, reminder) {
        if (!reminder.updatedAt) return ''
        return standardDateTime(reminder.updatedAt)
      },
    },
    {
      title: 'Practice',
      dataIndex: ['patient', 'practice', 'name'],
      key: 'practiceName',
    },
    {
      title: 'Error Message',
      dataIndex: ['errorMessage'],
      key: 'errorMessage',
      render(text, reminder) {
        if (!reminder.errorMessage) return '-'

        return <Text type="danger">{text}</Text>
      },
    },
    {
      title: 'Error Code',
      dataIndex: ['errorCode'],
      key: 'errorCode',
      render(text, reminder) {
        if (!reminder.errorCode) return '-'

        return <Text type="danger">{text}</Text>
      },
    },
  ]

  const mobileColumns = useMemo(
    () => [
      {
        title: 'Reminder',
        key: 'reminder',
        render(_, reminder) {
          return <ReminderMobileCard reminder={reminder} />
        },
      },
    ],
    [],
  )

  const mobileExpandable = useMemo(
    () => ({
      columnWidth: EXPAND_COLUMN_WIDTH,
      expandedRowRender: renderMobileDetails(),
    }),
    [],
  )

  const { pageSize, total } = useRemindersPagination()
  const reminders = useReminders()

  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader title="Reminders" />
      <Filters
        currentValues={conditions}
        onSubmit={handleSubmit}
        onReset={handleReset}
        extraFilter={rangeFilter()}
        showReminders
      />
      <Table
        size={isMobile ? 'small' : undefined}
        columns={isMobile ? mobileColumns : columns}
        showHeader={!isMobile}
        expandable={isMobile ? mobileExpandable : undefined}
        dataSource={reminders}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize,
          onChange: handlePageChange,
          total,
        }}
        rowKey="id"
      />
    </div>
  )
}
